/* ============================================================
   FLICKITY PRODUCT GALLERY - JavaScript
   assets/flickity-product-gallery.js
   ============================================================
   ONE slider, one set of cells.
 
   The markup used to carry a separate desktop and mobile set of
   images, so every phone downloaded the 800px desktop copies behind
   display:none and every desktop downloaded the 600px mobile copies.
   There is now a single .flickity-gallery__main, and the only thing
   that changes at the breakpoint is which options Flickity gets.
 
   Flickity also STARTS on the selected variant's cell
   (data-initial-index, rendered server-side). The old version always
   started on cell 0 and corrected after a 200ms timeout, which is
   what made the image blink to the default colour on reload.
 
   The body-wide MutationObserver that watched for location.href
   changes is gone — it ran its callback on every node insertion
   anywhere in the document, for the life of the page. history
   pushState/replaceState are patched once instead.
   ============================================================ */
 
(function () {
  'use strict';
 
  const MOBILE_BREAKPOINT = 750;
 
  /* Patch the History API once so programmatic URL changes fire an
     event. Fixed cost, rather than one callback per DOM mutation. */
  if (!window.__rccLocationPatched) {
    window.__rccLocationPatched = true;
    ['pushState', 'replaceState'].forEach(function (name) {
      const original = history[name];
      history[name] = function () {
        const result = original.apply(this, arguments);
        window.dispatchEvent(new Event('rcc:locationchange'));
        return result;
      };
    });
    window.addEventListener('popstate', function () {
      window.dispatchEvent(new Event('rcc:locationchange'));
    });
  }
 
  class FlickityProductGallery {
    constructor(container) {
      this.container = container;
      this.productId = container.dataset.productId;
      this.initialIndex = parseInt(container.dataset.initialIndex, 10) || 0;
 
      this.sliderEl = container.querySelector('.js-flickity-main');
      this.thumbsContainer = container.querySelector('.flickity-gallery__thumbs-col');
      this.thumbCells = container.querySelectorAll('.flickity-gallery__thumb-cell');
 
      this.flkty = null;
      this.isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
 
      this.init();
    }
 
    /* Desktop and mobile differ only in these options. */
    options() {
      const base = {
        initialIndex: this.initialIndex,
        prevNextButtons: false,
        pageDots: false,
        imagesLoaded: true,
        /* Mobile can't drag and has no dots — the only way to change
           image is the colour swatch — so loading neighbouring cells
           ahead of time is pure waste (4 × ~70 KiB per product page).
           `true` loads the selected cell only; the swatch swap triggers
           the next one on demand. Desktop keeps 2 because dragging works. */
        lazyLoad: this.isDesktop ? 2 : true,
        selectedAttraction: 0.08,
        friction: 0.6
      };
 
      if (this.isDesktop) {
        return Object.assign(base, {
          adaptiveHeight: true,
          draggable: true,
          cellAlign: 'left',
          contain: true
        });
      }
 
      return Object.assign(base, {
        adaptiveHeight: false,
        draggable: false,
        cellAlign: 'center',
        wrapAround: true
      });
    }
 
    init() {
      this.build();
      this.bindThumbs();
      this.bindResize();
      this.bindVariantChange();
 
      /* No syncToCurrentVariant() on load — Flickity already started
         on the right cell, so calling it would only force a reflow. */
    }
 
    build() {
      if (this.flkty || !this.sliderEl) return;
 
      this.flkty = new Flickity(this.sliderEl, this.options());
 
      if (!this.isDesktop) {
        /* Belt and braces: Flickity can re-enable dragging during
           setup on touch devices. */
        this.flkty.options.draggable = false;
        this.flkty.updateDraggable();
      }
 
      this.flkty.on('change', (index) => {
        this.highlightThumb(index);
        this.scrollThumbIntoView(index);
      });
 
      this.highlightThumb(this.flkty.selectedIndex);
 
      if (this.isDesktop) {
        this.flkty.on('settle', () => this.syncThumbHeight());
        setTimeout(() => this.syncThumbHeight(), 300);
      }
    }
 
    rebuild() {
      const keep = this.flkty ? this.flkty.selectedIndex : this.initialIndex;
      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }
      this.initialIndex = keep;
      this.build();
    }
 
    bindThumbs() {
      this.thumbCells.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          if (this.flkty) this.flkty.select(i);
          this.highlightThumb(i);
        });
      });
    }
 
    highlightThumb(index) {
      this.thumbCells.forEach((t, i) => {
        t.classList.toggle('is-nav-selected', i === index);
      });
    }
 
    scrollThumbIntoView(index) {
      /* The thumbnail column is hidden in base.css; skip the layout
         reads entirely while it isn't on screen. */
      if (!this.thumbsContainer || !this.thumbsContainer.offsetParent) return;
 
      const thumb = this.thumbCells[index];
      if (!thumb) return;
 
      requestAnimationFrame(() => {
        const containerRect = this.thumbsContainer.getBoundingClientRect();
        const thumbRect = thumb.getBoundingClientRect();
 
        const thumbTop = thumbRect.top - containerRect.top;
        const thumbBottom = thumbRect.bottom - containerRect.top;
        const h = containerRect.height;
 
        if (thumbTop < h * 0.3) {
          this.thumbsContainer.scrollTo({
            top: thumb.offsetTop - h + thumbRect.height + 10,
            behavior: 'smooth'
          });
        } else if (thumbBottom > h * 0.7) {
          this.thumbsContainer.scrollTo({
            top: thumb.offsetTop - 10,
            behavior: 'smooth'
          });
        }
      });
    }
 
    syncThumbHeight() {
      if (!this.flkty || !this.thumbsContainer) return;
      if (!this.thumbsContainer.offsetParent) return;
      const viewport = this.sliderEl.querySelector('.flickity-viewport');
      if (viewport) {
        this.thumbsContainer.style.maxHeight = viewport.offsetHeight + 'px';
      }
    }
 
    /* ============================================================
       RESPONSIVE
       ============================================================ */
    bindResize() {
      let resizeTimer;
 
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const nowDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
 
          if (nowDesktop !== this.isDesktop) {
            this.isDesktop = nowDesktop;
            this.rebuild();
          } else if (this.flkty) {
            this.flkty.resize();
            this.syncThumbHeight();
          }
        }, 200);
      });
    }
 
    /* ============================================================
       VARIANT CHANGE → SYNC GALLERY
       ============================================================ */
    bindVariantChange() {
      document.querySelectorAll('variant-selects, variant-radios').forEach((picker) => {
        picker.addEventListener('change', () => {
          setTimeout(() => this.syncToCurrentVariant(), 150);
        });
      });
 
      window.addEventListener('rcc:locationchange', () => {
        setTimeout(() => this.syncToCurrentVariant(), 100);
      });
 
      document.addEventListener('shopify:section:load', () => {
        setTimeout(() => this.syncToCurrentVariant(), 300);
      });
    }
 
    syncToCurrentVariant() {
      if (!this.flkty) return;
 
      const variantId = new URLSearchParams(window.location.search).get('variant');
      if (!variantId) return;
 
      const cells = this.container.querySelectorAll('.flickity-gallery__main-cell');
      const wanted = variantId.trim();
      let targetIndex = null;
 
      cells.forEach((cell, i) => {
        const ids = (cell.dataset.variantIds || '').split(',').map((id) => id.trim());
        if (ids.indexOf(wanted) !== -1) targetIndex = i;
      });
 
      if (targetIndex === null) return;
      if (this.flkty.selectedIndex === targetIndex) return;
 
      /* select(index, isWrapped, isInstant): isInstant swaps the image
         with no slide animation, like the collection card swatches. */
      this.flkty.select(targetIndex, !this.isDesktop, true);
      this.highlightThumb(targetIndex);
      this.scrollThumbIntoView(targetIndex);
    }
  }
 
  function initGalleries() {
    document.querySelectorAll('.flickity-gallery').forEach((el) => {
      if (!el._flickityGalleryInit) {
        el._flickityGalleryInit = true;
        new FlickityProductGallery(el);
      }
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }
 
  document.addEventListener('shopify:section:load', initGalleries);
})();
 
/* ============================================================
   LIGHTWEIGHT ZOOM LIGHTBOX (separate from gallery class)
   ============================================================ */
(function () {
  'use strict';
 
  function initZoom(gallery) {
    const overlay = gallery.querySelector('.flickity-zoom-overlay');
    if (!overlay) return;
    document.body.appendChild(overlay);
 
    const overlayImg = overlay.querySelector('.flickity-zoom-img');
    const closeBtn = overlay.querySelector('.flickity-zoom-close');
    const buttons = gallery.querySelectorAll('.flickity-gallery__zoom-btn');
 
    function currentImg() {
      return (
        gallery.querySelector('.flickity-gallery__main-cell.is-selected img') ||
        gallery.querySelector('.flickity-gallery__main-cell img')
      );
    }
 
    function open() {
      const img = currentImg();
      if (!img) return;
      const src = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
      if (!src) return;
 
      overlayImg.src = src;
      overlayImg.alt = img.alt || '';
      overlay.classList.add('is-open', 'is-zoomed');
 
      const centerScroll = function () {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            overlay.scrollLeft = (overlay.scrollWidth - overlay.clientWidth) / 2;
            overlay.scrollTop = (overlay.scrollHeight - overlay.clientHeight) / 2;
          });
        });
      };
      if (overlayImg.complete && overlayImg.naturalWidth) {
        centerScroll();
      } else {
        overlayImg.onload = centerScroll;
      }
 
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
 
    function close() {
      overlay.classList.remove('is-open', 'is-zoomed');
      overlay.setAttribute('aria-hidden', 'true');
      overlayImg.src = '';
      document.body.style.overflow = '';
    }
 
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        open();
      });
    });
 
    gallery.querySelectorAll('.flickity-gallery__main-cell img').forEach(function (cellImg) {
      cellImg.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        open();
      });
      cellImg.style.cursor = 'zoom-in';
    });
 
    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      close();
    });
 
    overlay.addEventListener('click', function (e) {
      if (e.target !== overlayImg) close();
    });
 
    overlayImg.addEventListener('click', function (e) {
      e.stopPropagation();
      if (window.innerWidth >= 750) close();
    });
 
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });
  }
 
  function initAll() {
    document.querySelectorAll('.flickity-gallery').forEach(function (g) {
      if (g._zoomInit) return;
      g._zoomInit = true;
      initZoom(g);
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();